import React, { useState, useRef, useEffect } from 'react';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'Gerade eben';
  if (m < 60) return `vor ${m} Min.`;
  if (h < 24) return `vor ${h} Std.`;
  return `vor ${d} Tag${d > 1 ? 'en' : ''}`;
}

const TYPE_META = {
  automation:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  icon: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/> },
  wiedervorlage:{ color: '#159c5f', bg: 'rgba(29,190,116,0.1)', icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
  task_due:     { color: '#22c55e', bg: 'rgba(34,197,94,0.1)',  icon: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></> },
  mention:      { color: '#8f7cc9', bg: 'rgba(143,124,201,0.12)', icon: <><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></> },
  system:       { color: '#6c7a70', bg: 'rgba(107,114,128,0.1)',icon: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></> },
};

export default function NotificationCenter({ notifications = [], onMarkRead, onMarkAllRead, onNavigate, onDismiss, leads = [], tasks = [] }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('notifs');
  const ref = useRef(null);

  const unread = notifications.filter(n => !n.read).length;

  const todayStr = new Date().toISOString().slice(0, 10);
  const dueTasks = tasks.filter(t => !t.done && t.dueDatetime && new Date(t.dueDatetime).toISOString().slice(0, 10) <= todayStr);
  const wvLeads  = leads.filter(l => l.wiedervorlage && String(l.wiedervorlage).slice(0, 10) <= todayStr);
  const urgentCount = dueTasks.length + wvLeads.length + unread;

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => { setOpen(o => !o); if (unread > 0 && !open) {} }}
        style={{ width: 36, height: 36, borderRadius: 9, background: open ? 'var(--accent-bg)' : 'var(--bg3)', border: `1.5px solid ${open ? 'var(--accent-border)' : 'var(--border)'}`, color: open ? 'var(--accent)' : 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flexShrink: 0, transition: 'all .12s' }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = 'var(--accent-bg)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent-border)'; } }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.borderColor = 'var(--border)'; } }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {urgentCount > 0 && (
          <div style={{ position: 'absolute', top: 3, right: 3, minWidth: 16, height: 16, borderRadius: 8, background: '#ef4444', border: '1.5px solid var(--bg2)', fontSize: 9, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
            {urgentCount > 9 ? '9+' : urgentCount}
          </div>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="gl-flyout" style={{ position: 'absolute', top: 46, right: 0, width: 380, maxHeight: 520, borderRadius: 18, overflow: 'hidden', zIndex: 500, display: 'flex', flexDirection: 'column' }}>
          {/* Prism shimmer */}
          <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9) 50%, transparent)', borderRadius: 999, pointerEvents: 'none', zIndex: 1 }} />
          <div style={{ padding: '14px 16px 0', borderBottom: '1px solid rgba(255,255,255,0.40)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>Benachrichtigungen</div>
              {unread > 0 && tab === 'notifs' && (
                <button onClick={onMarkAllRead} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>Alle gelesen</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 2 }}>
              {[['notifs', `Hinweise${unread > 0 ? ` (${unread})` : ''}`], ['tasks', `Aufgaben heute${dueTasks.length > 0 ? ` (${dueTasks.length})` : ''}`], ['wv', `WV fällig${wvLeads.length > 0 ? ` (${wvLeads.length})` : ''}`]].map(([id, lbl]) => (
                <button key={id} onClick={() => setTab(id)}
                  style={{ padding: '6px 10px', fontSize: 11, fontWeight: tab === id ? 700 : 400, color: tab === id ? 'var(--accent)' : 'var(--text3)', background: tab === id ? 'rgba(29,190,116,0.08)' : 'none', border: 'none', borderBottom: `2px solid ${tab === id ? 'var(--accent)' : 'transparent'}`, borderRadius: '6px 6px 0 0', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .12s' }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {tab === 'notifs' && (notifications.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 32, color: 'var(--text3)' }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.4"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text3)' }}>Keine Benachrichtigungen</div>
              </div>
            ) : notifications.map(n => {
              const meta = TYPE_META[n.type] || TYPE_META.system;
              return (
                <div key={n.id}
                  style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, background: n.read ? 'transparent' : 'rgba(29,190,116,0.03)', cursor: n.leadId ? 'pointer' : 'default', transition: 'background .1s' }}
                  onClick={() => { if (n.leadId) { onNavigate(n.leadId); setOpen(false); } onMarkRead(n.id); }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(29,190,116,0.03)'; }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: meta.bg, border: `1px solid ${meta.color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth="2">{meta.icon}</svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: 'var(--text)', lineHeight: 1.3 }}>{n.title}</div>
                      {!n.read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#159c5f', flexShrink: 0, marginTop: 4 }} />}
                    </div>
                    {n.message && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>{n.message}</div>}
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{timeAgo(n.createdAt)}</div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); onDismiss(n.id); }}
                    style={{ width: 20, height: 20, borderRadius: 4, border: 'none', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: 0.5, marginTop: 4 }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'var(--bg4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.background = 'transparent'; }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              );
            }))}

            {tab === 'tasks' && (dueTasks.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>✅ Keine fälligen Aufgaben heute</div>
            ) : dueTasks.map(t => {
              const overdue = new Date(t.dueDatetime) < new Date();
              return (
                <div key={t.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: overdue ? '#ef4444' : '#f59e0b', marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{t.title || t.type}</div>
                    {t.note && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{String(t.note).slice(0, 60)}</div>}
                    <div style={{ fontSize: 11, color: overdue ? '#ef4444' : '#f59e0b', marginTop: 2, fontWeight: 600 }}>
                      {overdue ? 'Überfällig' : 'Heute fällig'} · {t.dueDatetime ? new Date(t.dueDatetime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                </div>
              );
            }))}

            {tab === 'wv' && (wvLeads.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>✅ Keine fälligen Wiedervorlagen heute</div>
            ) : wvLeads.map(l => {
              const wv = String(l.wiedervorlage).slice(0, 10);
              const overdue = wv < todayStr;
              return (
                <div key={l.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => { onNavigate?.(l.id); setOpen(false); }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: overdue ? '#ef4444' : '#f59e0b', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l.company}</div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: overdue ? '#ef4444' : '#f59e0b', flexShrink: 0 }}>
                    {overdue ? `Überfällig · ${wv}` : 'Heute'}
                  </div>
                </div>
              );
            }))}
          </div>
        </div>
      )}
    </div>
  );
}
